export interface CoffeeRecord {
  id: string;
  title: string;
  slug: string;
  processType: string;
  flavorNotes: string;
  tastingNotes: string;
  visualDescription: string;
  imageUrl?: string;
  fermentationMethod: string;
  fermentationDuration: string;
  dryingMethod: string;
  screenSize: string;
  price: string;
  packagingInfo: string;
  quantityInStock: string;
  pricePerKg: string;
  availabilityStatus: string;
  additionalImages: string[];
  cuppingScore: number;
  detailedDescription: string;
  productionInformation: string;
  cherrySelection: string;
  fermentationDetails: string;
  dryingDetails: string;
  region: string;
  location: string;
  varieties: string;
  harvestPeriod: string;
  altitude: string;
  isFeatured?: boolean;
  sortOrder?: number;
  gradeBreakdown?: Record<string, number>;
  gradePricing?: Record<string, number>;
}

export interface InquiryData {
  name: string;
  email: string;
  company?: string;
  subject?: string;
  message: string;
  coffeeName?: string;
  fromApp?: string;
  website?: string; // honeypot
  formRenderedAt?: number;
}

export interface SampleOrderData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  country?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  requestedCoffees?: string[];
  notes?: string;
  orderDate?: string;
  status?: string;
  source?: string;
  website?: string; // honeypot
  formRenderedAt?: number;
}

let coffeeCache: { data: CoffeeRecord[]; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const fetchCoffeeRecords = async (useCache: boolean = true): Promise<CoffeeRecord[]> => {
  if (useCache && coffeeCache && (Date.now() - coffeeCache.timestamp) < CACHE_DURATION) {
    return coffeeCache.data;
  }

  try {
    const res = await fetch('/api/coffees');
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data: CoffeeRecord[] = await res.json();
    coffeeCache = { data, timestamp: Date.now() };
    return data;
  } catch (error) {
    console.error('Failed to fetch coffees:', error);
    if (coffeeCache) return coffeeCache.data;
    return [];
  }
};

export const fetchCoffeeBySlug = async (slug: string): Promise<CoffeeRecord | null> => {
  try {
    const res = await fetch(`/api/coffees?slug=${encodeURIComponent(slug)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch coffee:', error);
    const coffees = await fetchCoffeeRecords();
    return coffees.find(c => c.slug === slug) || null;
  }
};

export const submitInquiry = async (inquiryData: InquiryData): Promise<{ success: boolean; error?: string }> => {
  try {
    const dbRes = await fetch('/api/inquiries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: inquiryData.name,
        email: inquiryData.email,
        company: inquiryData.company,
        subject: inquiryData.subject,
        message: inquiryData.message,
        coffeeName: inquiryData.coffeeName,
        website: inquiryData.website,
        formRenderedAt: inquiryData.formRenderedAt,
      }),
    });

    if (!dbRes.ok) {
      const err = await dbRes.json();
      return { success: false, error: err.error || 'Failed to submit inquiry' };
    }

    const result = await dbRes.json();

    await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'contact', id: result.id }),
    }).catch(() => {});

    return { success: true };
  } catch (error) {
    console.error('Submit inquiry error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

export const submitSampleOrder = async (
  orderData: SampleOrderData
): Promise<{ success: boolean; orderId?: string; error?: string }> => {
  try {
    const dbRes = await fetch('/api/sample-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: orderData.name,
        email: orderData.email,
        company: orderData.company,
        phone: orderData.phone,
        country: orderData.country,
        addressLine1: orderData.addressLine1,
        addressLine2: orderData.addressLine2,
        city: orderData.city,
        stateProvince: orderData.stateProvince,
        postalCode: orderData.postalCode,
        requestedCoffees: orderData.requestedCoffees,
        notes: orderData.notes,
        website: orderData.website,
        formRenderedAt: orderData.formRenderedAt,
      }),
    });

    if (!dbRes.ok) {
      const err = await dbRes.json();
      return { success: false, error: err.error || 'Failed to submit order' };
    }

    const result = await dbRes.json();

    await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'sample-order', id: result.orderId }),
    }).catch(() => {});

    return { success: true, orderId: result.orderId };
  } catch (error) {
    console.error('Submit order error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

export const requestSample = (coffeeName?: string, userAgent?: string) => {
  const subject = coffeeName ? `Sample Request: ${coffeeName}` : 'Coffee Samples Request';
  const body = coffeeName
    ? `Hi Sidney,\n\nI'm interested in requesting a sample of ${coffeeName}. Please let me know about availability, pricing, and shipping details.\n\nThank you!`
    : `Hi Sidney,\n\nI'm interested in requesting coffee samples from Lot 20. Please let me know about your current offerings, pricing, and shipping details.\n\nThank you!`;

  const mailtoUrl = `mailto:sidney@lot20.coffee?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent || navigator.userAgent);

  if (isMobile) {
    window.location.href = mailtoUrl;
  } else {
    try {
      window.location.href = mailtoUrl;
      setTimeout(() => {
        const shouldShowFallback = confirm("If your email client didn't open, would you like to copy the email details to your clipboard?");
        if (shouldShowFallback) {
          const emailDetails = `To: sidney@lot20.coffee\nSubject: ${subject}\n\n${body}`;
          navigator.clipboard.writeText(emailDetails).then(() => {
            alert('Email details copied to clipboard!');
          }).catch(() => {
            const textArea = document.createElement('textarea');
            textArea.value = emailDetails;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Email details copied to clipboard!');
          });
        }
      }, 2000);
    } catch {
      const emailDetails = `To: sidney@lot20.coffee\nSubject: ${subject}\n\n${body}`;
      navigator.clipboard.writeText(emailDetails).then(() => {
        alert('Email client not available. Email details copied to clipboard!');
      });
    }
  }
};
